import { ControllerCode, MicroController, NotificationContext } from '#models/MicroController.model';
import { MicroControllersFamily } from '#models/MicroControllersFamily.model';
import { EsgQuizUpdateFragmentComponent } from '#pages/esg/esg-quiz-update-fragment/esg-quiz-update-fragment.component';
import { hasCheckData, isManualCheckRequired } from '#services/cve/ControllerDetails';
import { VettingIteration } from '#services/cve/VettingIteration';

import { microControllers } from './microControllers.data';
import { SanctionsSFACFamilyNotificationComponent } from './microControllersNotifications.data';

export enum FamiliesEnum {
  SANCTIONS = 'SANCTIONS',
  CREDIT_RISK = 'CREDIT_RISK',
  PROSPECT_ELIGIBILITY_CHECK = 'PROSPECT_ELIGIBILITY_CHECK',
  GERL_SCREENING = 'GERL_SCREENING',
  ESG_SCREENING = 'ESG_SCREENING',
  REPUTATIONAL_SCREENING = 'REPUTATIONAL_SCREENING',
}

export type FamilyCode = keyof typeof FamiliesEnum;

export const microControllersFamilies: {
  [key in FamiliesEnum]: MicroControllersFamily;
} = {
  [FamiliesEnum.SANCTIONS]: {
    i18n: 'family.sanctions',
    subFamilies: [
      {
        i18n: 'family.sanctions',
        microControllers: [microControllers['EconomicSanctions'], microControllers['PoliticalExposedPerson']],
      },
    ],
    link: 'sanctions',
    computeNotifications: (_, controllers: MicroController[]) => {
      if (
        controllers.some(
          (control) =>
            hasCheckData(control.controllerDetails) &&
            control.controllerDetails.check.controlData.requestData?.buCode === 'SFAC',
        )
      ) {
        return [{ message: SanctionsSFACFamilyNotificationComponent, context: NotificationContext.INFO }];
      }
      return [];
    },
  },
  [FamiliesEnum.CREDIT_RISK]: {
    i18n: 'family.credit-risk',
    subFamilies: [
      {
        i18n: 'family.credit-risk',
        microControllers: [microControllers['CompanyGrade']],
      },
    ],
    link: 'credit-risk',
  },
  [FamiliesEnum.PROSPECT_ELIGIBILITY_CHECK]: {
    i18n: 'family.prospect-eligibility-check',
    subFamilies: [
      {
        i18n: 'family.prospect-eligibility-check',
        microControllers: [
          microControllers['AccountDetails'],
          microControllers['AccountOwner'],
          microControllers['BuExchangeGroup'],
          microControllers['CompanyConsortOrCoop'],
          microControllers['MncList'],
          microControllers['PreviousContract'],
          microControllers['PreviousNegociation'],
          microControllers['ProspectLocation'],
          microControllers['UnpaidPremiumAndFees'],
        ],
      },
    ],
    link: 'prospect-eligibility-check',
  },
  [FamiliesEnum.GERL_SCREENING]: {
    i18n: 'family.gerl-screening',
    subFamilies: [
      {
        i18n: 'family.gerl-screening',
        microControllers: [microControllers['Gerl']],
      },
    ],
    link: 'gerl',
    specificMessages: (iterationData: VettingIteration) => {
      const esgGerlQuizComment = iterationData.customProperties.esgGerlQuizComment;
      return esgGerlQuizComment ? [{ text: esgGerlQuizComment, titleI18n: 'esg-gerl.escalation-quiz' }] : [];
    },
  },
  [FamiliesEnum.ESG_SCREENING]: {
    i18n: 'family.esg',
    subFamilies: [
      {
        i18n: 'family.esg.trade-sector',
        microControllers: [microControllers['TradeSector']],
      },
      {
        i18n: 'family.esg.energy-policy',
        microControllers: [microControllers['EnergyPolicy']],
      },
      {
        i18n: 'family.esg.sensitive-business',
        microControllers: [microControllers['SensitiveBusiness']],
      },
      {
        i18n: 'family.esg.sensitive-country',
        microControllers: [microControllers['SensitiveCountry']],
      },
    ],
    link: 'esg',
    specificMessages: (iterationData: VettingIteration) => {
      const customerActivityDescription =
        iterationData.customProperties.supportRequestData?.customerActivityDescription;
      const esgGerlQuizComment = iterationData.customProperties?.esgGerlQuizComment;
      const hasNoManualControls = iterationData.controls
        .filter((ctrl) => isControlOfFamily(FamiliesEnum.ESG_SCREENING, ctrl.controlCode))
        .every((ctrl) => !isManualCheckRequired(ctrl));
      return [
        customerActivityDescription
          ? { text: customerActivityDescription, titleI18n: 'esg.customer-activity' }
          : undefined,
        esgGerlQuizComment && hasNoManualControls
          ? { text: esgGerlQuizComment, titleI18n: 'esg-gerl.escalation-quiz' }
          : undefined,
      ].filter((s) => s !== undefined);
    },
    computeNotifications: (iterationData: VettingIteration) => {
      const hasManualControls = iterationData.controls
        .filter((ctrl) => isControlOfFamily(FamiliesEnum.ESG_SCREENING, ctrl.controlCode))
        .some(isManualCheckRequired);
      return [
        hasManualControls ? { context: NotificationContext.NONE, message: EsgQuizUpdateFragmentComponent } : undefined,
      ].filter((n) => n !== undefined);
    },
  },
  [FamiliesEnum.REPUTATIONAL_SCREENING]: {
    i18n: 'family.reputational-screening',
    subFamilies: [
      {
        i18n: 'family.reputational-screening',
        microControllers: [microControllers['ReputationalRisk']],
      },
    ],
    link: 'reputational-screening',
  },
};

export const isControlOfFamily = (family: FamiliesEnum, controlCode: ControllerCode) => {
  const familyControlCodes = microControllersFamilies[family].subFamilies.flatMap((sub) =>
    sub.microControllers.map((ctrl) => ctrl.code),
  );
  return familyControlCodes.includes(controlCode);
};
